import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const FOOD_IMG =
  "https://images.pexels.com/photos/20593400/pexels-photo-20593400.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export function Marquee() {
  const items = [
    "Nutrition & sustainable eating",
    "Weight & energy",
    "Stress, sleep & mindset",
    "1-on-1 coaching",
    "Dubai · online worldwide",
  ];
  const Row = ({ hidden }) => (
    <div className="flex items-center flex-none" aria-hidden={hidden || undefined}>
      {items.map((t) => (
        <span key={t} className="inline-flex items-center gap-10 pr-10 whitespace-nowrap">
          <span className="font-displayb italic font-light text-2xl text-cream/90">{t}</span>
          <span className="w-2 h-2 rounded-full bg-clay" />
        </span>
      ))}
    </div>
  );
  return (
    <div className="bg-forest py-6 overflow-hidden" data-testid="marquee-band-b">
      <div className="flex w-max animate-marquee">
        <Row />
        <Row hidden />
      </div>
    </div>
  );
}

const ITEMS = [
  {
    n: "01",
    t: "Nutrition & sustainable eating",
    d: "No extreme diets or rigid rules. An eating pattern that fits your real life — simple enough to follow, flexible enough to last.",
    img: FOOD_IMG,
    alt: "Fresh, colourful nourish bowl",
  },
  {
    n: "02",
    t: "Weight & energy, no guesswork",
    d: "Steady progress over quick fixes. A clear, step-by-step plan, so you always know what to do next — and why it's working.",
    img: "/images/ak_w1.jpg",
    alt: "Akanksha practising yoga at home",
  },
  {
    n: "03",
    t: "Stress, sleep & mindset",
    d: "Stress and self-doubt derail progress as much as any diet. We manage both together, so consistency feels possible.",
    img: "/images/ak_w3.jpg",
    alt: "Akanksha meditating outdoors",
  },
];

export function Specialties() {
  return (
    <section id="specialties" className="bg-cream py-28" data-testid="specialties-section-b">
      <div className="max-w-[1360px] mx-auto px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <Reveal>
            <h2 className="font-displayb font-light text-[clamp(2.4rem,5vw,4.2rem)] leading-[1.02]">
              How we can <em className="italic text-clay">help</em>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-xs text-forest/60">Three focus areas, one goal — habits that hold up in real life.</p>
          </Reveal>
        </div>
        <div className="border-t border-forest/15">
          {ITEMS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <a
                href="#contact"
                data-testid={`specialty-row-${s.n}`}
                className="group grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_1fr_auto_auto] items-center gap-5 sm:gap-8 py-8 border-b border-forest/15 transition-colors hover:bg-forest/[0.04]"
              >
                <span className="font-displayb italic text-clay text-xl w-10">{s.n}</span>
                <span>
                  <span className="block font-displayb text-2xl sm:text-3xl leading-snug">{s.t}</span>
                  <span className="block mt-2 text-forest/60 leading-relaxed max-w-xl">{s.d}</span>
                </span>
                <img
                  src={s.img}
                  alt={s.alt}
                  loading="lazy"
                  className="hidden sm:block w-20 h-20 rounded-full object-cover"
                />
                <span className="hidden sm:grid place-items-center w-11 h-11 rounded-full border border-forest/25 text-forest transition-all duration-300 group-hover:bg-forest group-hover:text-cream group-hover:rotate-45">
                  <ArrowUpRight size={18} />
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const PRINCIPLES = [
  {
    t: "Bio-individual",
    d: "There is no one plan. Your protocol is built for your body and your life — not the average person.",
  },
  {
    t: "Evidence-informed",
    d: "UK-trained and science-first. What's in your plan is there because it works, not because it's trendy.",
  },
  {
    t: "Real-life sustainable",
    d: "If it doesn't fit your actual week — your work, your family, your kitchen — it doesn't go in your plan.",
  },
];

export function Philosophy() {
  return (
    <section className="bg-forest text-cream py-28 relative overflow-hidden" data-testid="philosophy-section">
      <div
        aria-hidden="true"
        className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full bg-[radial-gradient(circle,rgba(192,122,94,0.18),transparent_65%)]"
      />
      <div className="relative max-w-[1360px] mx-auto px-6 lg:px-12">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-cream/50">The Well&Truly philosophy</p>
          <h2 className="mt-5 font-displayb font-light text-[clamp(2.4rem,5vw,4.2rem)] leading-[1.05] max-w-3xl">
            Guided by evidence.
            <br />
            <em className="italic text-clay">Delivered with care.</em>
          </h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-3 gap-10">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.t} delay={i * 0.12} className="border-t border-cream/20 pt-8">
              <span className="font-displayb italic text-clay text-lg">0{i + 1}</span>
              <h3 className="mt-3 font-displayb text-2xl">{p.t}</h3>
              <p className="mt-3 text-cream/65 leading-relaxed">{p.d}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-14 flex flex-wrap gap-3">
          {["Certified — trained in the UK", "Dubai based", "In person & online"].map((c) => (
            <span key={c} className="rounded-full border border-cream/25 px-5 py-2 text-sm text-cream/85">
              {c}
            </span>
          ))}
        </Reveal>
      </div>
    </section>
  );
}
